import type { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';
import { ALL_SBEF_GROUP_KEYS } from '@features/sbef/sbef-group-options';

/**
 * Compact label for a subset of selected SBEF groups (numeric ranges, custom IDs).
 */
export const formatSbefSummary = (
  keys: readonly SbefScopeGroupKey[],
): string => {
  const numericKeys = keys
    .filter((k) => /^\d$/.test(k))
    .map(Number)
    .sort((a, b) => a - b);

  const ranges: string[] = [];
  let i = 0;
  while (i < numericKeys.length) {
    let j = i;
    while (j + 1 < numericKeys.length) {
      const cur = numericKeys[j];
      const next = numericKeys[j + 1];
      if (cur === undefined || next === undefined || next !== cur + 1) break;
      j++;
    }
    const start = numericKeys[i];
    const end = numericKeys[j];
    ranges.push(j > i ? `${start}–${end}` : String(start));
    i = j + 1;
  }

  return ranges.join(', ');
};

/**
 * Returns true when SBEF filtering has no effect — i.e. when zero groups are
 * selected (treated as "show all") or all groups are selected. Both extremes
 * collapse to the same no-filter state.
 */
export const isSbefFilterNoop = (
  selectedSbefGroups: readonly SbefScopeGroupKey[],
): boolean =>
  selectedSbefGroups.length === 0 ||
  selectedSbefGroups.length === ALL_SBEF_GROUP_KEYS.length;

/**
 * Trigger label for the SBEF scope selector. Returns "All" when filtering is off.
 */
export const getSbefSelectionSummary = (
  selectedSbefGroups: readonly SbefScopeGroupKey[],
): string =>
  isSbefFilterNoop(selectedSbefGroups)
    ? 'All'
    : formatSbefSummary(selectedSbefGroups);
